var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : new P(function (resolve) { resolve(result.value); }).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import vegaEmbed from 'vega-embed';
import { html, LitElement, property } from '@polymer/lit-element';
import { microTask } from '@polymer/polymer/lib/utils/async.js';
import { Debouncer } from '@polymer/polymer/lib/utils/debounce.js';
// default style for vega-embed
const embedStyle = `.vega-embed {
  position: relative;
  display: inline-block;
  padding-right: 38px;
}

.vega-embed .vega-actions-wrapper {
  display: inline-flex;
  position: absolute;
  top: 0;
  right: 0;
  padding: 6px;
  z-index: 1000;

  opacity: 0.2;
  background: white;
  box-shadow: 1px 1px 3px rgba(0, 0, 0, 0.1);
  color: #1b1e23;
  border: 1px solid #aaa;
  border-radius: 999px;
  transition: opacity 0.4s ease-in;
}

.vega-embed:hover .vega-actions-wrapper {
  transition: opacity 0.2s ease;
  opacity: 1;
}

.vega-embed .vega-actions {
  position: absolute;
  top: 0;
  right: 0;
  display: none;
  flex-direction: column;

  padding-bottom: 8px;
  padding-top: 8px;
  border-radius: 4px;
  box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.2);
  border: 1px solid #d9d9d9;
  background: white;
}

.vega-embed .vega-actions-wrapper:hover {
  background: transparent;
  color: transparent;
  border: none;
  box-shadow: none;
}

.vega-embed .vega-actions-wrapper:hover .vega-actions {
  display: flex;
}

.vega-embed .vega-actions a {
  padding: 8px 16px;
  font-family: sans-serif;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  color: #434a56;
  text-decoration: none;
}

.vega-embed .vega-actions a:hover {
  background-color: #f7f7f9;
  color: #1b1e23;
}`;
// check if vegaEmbed is already loaded
export const embed = !!window.vegaEmbed
    ? window.vegaEmbed
    : vegaEmbed;
// vega-embed did not export this constant
const I18N = {
    COMPILED_ACTION: 'View Vega',
    EDITOR_ACTION: 'Open in Vega Editor',
    PNG_ACTION: 'Save as PNG',
    SOURCE_ACTION: 'View Source',
    SVG_ACTION: 'Save as SVG'
};
/** Cast any value into a boolean if appropriate, else return a string. */
export function asBoolOrGeneric(value) {
    if (typeof value === 'boolean')
        return value;
    const v = value
        .toString()
        .trim()
        .toLowerCase();
    if (v == '' || v == '1' || v == 'true')
        return true;
    if (v == '0' || v == 'false')
        return false;
    return value;
}
function asObjectOrGeneric(value) {
    if (typeof value === 'object')
        return value;
    try {
        return JSON.parse(value);
    }
    catch (error) {
        return value;
    }
}
function asArray(value) {
    if (Array.isArray(value)) {
        return value;
    }
    return value.split(/[;, |]/gim).map(str => str.trim());
}
const IGNORED = new Set([
    'showExport',
    'showSource',
    'showCompiled',
    'showEditor',
    'spec',
    'opts',
    'defaultStyle',
    'ele',
    'view',
    'embedStyle',
    'observeState',
    'observeData',
    'dataHandler',
    'dataListeners',
    'debouncer'
]);
/**
`vega-embed` is a custom element that wraps around the [VegaEmbed](https://github.com/vega/vega-embed) micro-lib
to visualize `[Vega](https://vega.github.io/vega)` and `[Vega-Lite](https://vega.github.io/vega-lite)` views.

## Usage
### Installing the npm package
```bash
npm i @polymer-vis/vega-elements --save
```

### Usage in html
```html
<vega-embed spec="/path/to/some/vega/spec.json"></vega-embed>
```

### Usage in online code sharing hosts (e.g. jsfiddle, gist, etc)
```html
<!-- Import Vega 4 & Vega-Lite 2 -->
<script src="https://cdn.jsdelivr.net/npm/vega@[VERSION]"></script>
<script src="https://cdn.jsdelivr.net/npm/vega-lite@[VERSION]"></script>

<!-- Load a bundled version of vega-embed custom element with jsdelivr -->
<script src="https://cdn.jsdelivr.net/npm/@polymer-vis/vega-elements/dist/vega-embed.bundled.min.js"></script>

<vega-embed spec="/path/to/some/vega/spec.json"></vega-embed>

<script>
// create a new vega-embed element programmatically
const ele = new VegaElements.VegaEmbed();
// set attribute
ele.setAttribute("show-export", true);
// set property
ele.showSource = true;
// set some spec
ele.spec = {...};
// attach to DOM
document.body.appendChild(ele);
</script>
```

### Usage with [`lit-html`](https://github.com/Polymer/lit-html)
```js
// import lit-html
import {render, html} from "lit-html";

// import the vega-embed element
import "./node_modules/vega-elements/vega-embed.js";

// vega-lite specification
conse scatterplot = {
  "$schema": "https://vega.github.io/schema/vega-lite/v2.json",
  "description": "A scatterplot showing horsepower and miles per gallons for various cars.",
  "data": {"url": "data/cars.json"},
  "mark": {"type": "point", "tooltip": {"content": "data"}},
  "encoding": {
    "x": {"field": "Horsepower", "type": "quantitative"},
    "y": {"field": "Miles_per_Gallon", "type": "quantitative"},
    "tooltip": [
      {"field": "Horsepower", "type": "quantitative"},
      {"field": "Miles_per_Gallon", "type": "quantitative"}
    ]
  }
};

// create the factory method to create a html template result
const embedTmpl = (spec, opts) => html`<vega-embed spec=${spec} opts=${opts}></vega-embed>`;

// render the template result to document body
render(embedTmpl(scatterplot, {tooltip: true, renderer: "canvas"}), document.body);
```

## UMD bundles
`vega-elements` provides 2 pre-build UMD distributions (under the named scope `VegaElements`) for `vega-embed` custom element.

- `./dist/vega-embed.min.js`: Minimal bundle with only [`@polymer/lit-element`](https://github.com/Polymer/lit-element) and `@polymer-vis/vega-elements`.
  Vega, Vega-Lite, and [`vega-embed`](https://github.com/vega/vega-embed) should be imported separately.

- `./dist/vega-embed.bundled.min.js`: Bundle with [`@polymer/lit-element`](https://github.com/Polymer/lit-element),
  [`vega-embed`](https://github.com/vega/vega-embed), and `@polymer-vis/vega-elements`. Vega or/and Vega-Lite should be
  imported separately.


@element vega-embed
@demo index.html Examples
 */
export class VegaEmbed extends LitElement {
    constructor() {
        super(...arguments);
        /**
         * Show the control to export SVG/PNG.
         * @type {Boolean}
         */
        this.showExport = false;
        /**
         * Show the control to display the source specification.
         * @type {Boolean}
         */
        this.showSource = false;
        /**
         * Show the control to display the compiled vega specification (for vega-lite source).
         * @type {Boolean}
         */
        this.showCompiled = false;
        /**
         * Show the control to display the specification in an editor at
         * https://vega.github.io/editor
         * @type {Boolean}
         */
        this.showEditor = false;
        /**
         * Whether to show a tooltip (with the corresponding `tooltip` field).
         * Alternatively, you can pass in a `TooltipOptions` or a custom `TooltipHandler`.
         *
         * See [vega-tooltip](https://github.com/vega/vega-tooltip) for more details.
         * @type {Boolean|Object}
         */
        this.tooltip = false;
        /**
         * Whether to apply the default style for the controls. Alternatively, you can pass in the
         * style directly. Default to `true`.
         * @type {Boolean|String}
         */
        this.defaultStyle = true;
        /**
         * Set `true` to show all actions: `export`, `source`, `compiled`, and `editor.
         * Defaults to `true`.
         * Set `false` to hide all actions.
         * Otherwise, you can also pass in an Object with a boolean for each of the following
         * available actions.
         *
         * @example
         * ```html
         * <vega-embed actions='{"export": true, "source": false}'></vega-embed>
         * ```
         *
         */
        this.actions = {
            export: true,
            source: true,
            compiled: true,
            editor: true
        };
        /**
         * Sets the current log level. See [Vega docs](https://vega.github.io/vega/docs/api/view/#view_logLevel) for details.
         * (0: None, 1: Error, 2: Warn, 3: Info, 4: Debug)
         *
         * @type {Number}
         */
        this.logLevel = 0;
        /**
         * The renderer to use for the view. One of `canvas` (default) or `svg`.
         *
         * @type {String}
         */
        this.renderer = 'canvas';
        /**
         * The number by which to multiply the width and height (default 1) of an exported PNG or SVG image.
         *
         * @type {Number}
         */
        this.scaleFactor = 1;
        /**
         * Enable `hover` event processing. `Hover` event processing is enabled on Vega by default.
         * Alternatively, you can provide an Object with keys (`hoverSet` and/or `updateSet`) to specify which named
         * encoding sets to invoke upon mouseover and mouseout.
         *
         * @type {Boolean|{hoverSet?: String; updateSet?: String}}
         */
        this.hover = true;
        /**
         * Use [runAsync](https://vega.github.io/vega/docs/api/view/#view_runAsync) instead of [run](https://vega.github.io/vega/docs/api/view/#view_run).
         *
         * @type {Boolean}
         */
        this.runAsync = false;
        /**
         * Instead of configuring `vega-embed` through its properties or attributes,
         * you can also pass in the entire configuration Object directly.
         * See [Vega-Embed docs](https://github.com/vega/vega-embed#options) for more details.
         *
         * @type {Object}
         */
        this.opts = {};
        /**
         * The default style for the `vega-embed` actions. Do not modify unless you know
         * what you are doing.
         *
         * @type {String}
         */
        this.embedStyle = embedStyle;
        /**
         * Set `observe-state` flag for the element to dispatch custom event
         * `state` whenever the view is updated.
         *
         * @type {Boolean}
         */
        this.observeState = false;
        /**
         * List of dataset that has a listener.
         *
         * @type String[]
         */
        this.dataListeners = [];
        /**
         * Debouncer
         */
        this.debouncer = null;
        /**
         * Fired when `observe-data` is set.
         *
         * @event data
         * @param {{name: String, value: Array}} dataset.
         */
        /**
         * Default dataHandler.
         */
        this.dataHandler = (name, value) => {
            this.dispatchEvent(new CustomEvent('data', { detail: { name, value } }));
        };
    }
    static get properties() {
        return { /*A JSON object or url to the Vega or Vega-Lite specification.*/
            spec: { attribute: 'spec', type: asObjectOrGeneric }, /*Show the control to export SVG/PNG.*/
            showExport: { attribute: 'show-export', type: Boolean }, /*Show the control to display the source specification.*/
            showSource: { attribute: 'show-source', type: Boolean }, /*Show the control to display the compiled vega specification (for vega-lite source).*/
            showCompiled: { attribute: 'show-compiled', type: Boolean }, /*Show the control to display the specification in an editor at
            https://vega.github.io/editor*/
            showEditor: { attribute: 'show-editor', type: Boolean }, /*Whether to parse the specification as `vega` or `vega-lite`.
            If not provided, `vega-embed` will try to infer from the `$schema` field,
            otherwise it will default to `vega`.*/
            mode: { attribute: true, type: String }, /*The theme to apply: `excel` | `ggplot2` | `quartz` | `vox` | `dark`.*/
            theme: { attribute: true, type: String }, /*Whether to show a tooltip (with the corresponding `tooltip` field).
            Alternatively, you can pass in a `TooltipOptions` or a custom `TooltipHandler`.
            
            See [vega-tooltip](https://github.com/vega/vega-tooltip) for more details.*/
            tooltip: { attribute: true, type: asBoolOrGeneric }, /*Whether to apply the default style for the controls. Alternatively, you can pass in the
            style directly. Default to `true`.*/
            defaultStyle: { attribute: 'default-style', type: asBoolOrGeneric }, /*Set `true` to show all actions: `export`, `source`, `compiled`, and `editor.
               * Defaults to `true`.
               * Set `false` to hide all actions.
               * Otherwise, you can also pass in an Object with a boolean for each of the following
               * available actions.
               *
               * @example
               * ```html
            <vega-embed actions='{"export": true, "source": false}'></vega-embed>
            ```
               *
               **/
            actions: { attribute: true, type: asBoolOrGeneric }, /*Sets the current log level. See [Vega docs](https://vega.github.io/vega/docs/api/view/#view_logLevel) for details.
            (0: None, 1: Error, 2: Warn, 3: Info, 4: Debug)*/
            logLevel: { attribute: 'log-level', type: Number }, /*Sets a custom Vega `loader` or `loader` options.
            See [Vega docs](https://vega.github.io/vega/docs/api/view/#view) for details.*/
            loader: { attribute: true, type: asObjectOrGeneric }, /*The renderer to use for the view. One of `canvas` (default) or `svg`.*/
            renderer: { attribute: true, type: String }, /*A callback function that can modify an input specification before it is parsed.*/
            onBeforeParse: { attribute: 'on-before-parse', type: Function }, /*Sets the view width in pixels. Note that Vega-Lite overrides this option.*/
            width: { attribute: true, type: Number }, /*Sets the view height in pixels. Note that Vega-Lite overrides this option.*/
            height: { attribute: true, type: Number }, /*Sets the view padding in pixels.
            See [Vega docs](https://vega.github.io/vega/docs/api/view/#view_padding) for details.*/
            padding: { attribute: true, type: asObjectOrGeneric }, /*The number by which to multiply the width and height (default 1) of an exported PNG or SVG image.*/
            scaleFactor: { attribute: 'scale-factor', type: Number }, /*Either a URL string from which to load a [Vega](https://vega.github.io/vega/docs/config/)/Vega-Lite
            or [Vega-Lite](https://vega.github.io/vega-lite/docs/config.html) configuration file, or
            a JSON object to override the default configuration options.*/
            config: { attribute: true }, /*HTML to inject into the `` tag of the page generated by the "View Source" and "View Vega" action link.
            For example, this can be used to add code for syntax highlighting.*/
            sourceHeader: { attribute: 'source-header', type: String }, /*HTML to inject into the end of the page generated by the "View Source" and "View Vega" action link.
            The text will be added immediately before the closing `` tag.*/
            sourceFooter: { attribute: 'source-footer', type: String }, /*The URL at which to open embedded Vega specs in a Vega editor.
            Defaults to "http://vega.github.io/editor/".
            Internally, Vega-Embed uses HTML5 postMessage to pass the specification information to the editor.*/
            editorUrl: { attribute: 'editor-url', type: String }, /*Enable `hover` event processing. `Hover` event processing is enabled on Vega by default.
            Alternatively, you can provide an Object with keys (`hoverSet` and/or `updateSet`) to specify which named
            encoding sets to invoke upon mouseover and mouseout.*/
            hover: { attribute: true, type: asBoolOrGeneric }, /*Use [runAsync](https://vega.github.io/vega/docs/api/view/#view_runAsync) instead of [run](https://vega.github.io/vega/docs/api/view/#view_run).*/
            runAsync: { attribute: 'run-async', type: Boolean }, /*This property maps keys (`COMPILED_ACTION`, `EDITOR_ACTION`, `PNG_ACTION`, `SOURCE_ACTION`, `SVG_ACTION`)
            to string values for the action's text. By default, the text is in English.*/
            i18n: { attribute: true, type: asObjectOrGeneric }, /*Instead of configuring `vega-embed` through its properties or attributes,
            you can also pass in the entire configuration Object directly.
            See [Vega-Embed docs](https://github.com/vega/vega-embed#options) for more details.*/
            opts: { attribute: true, type: asObjectOrGeneric }, /*The default style for the `vega-embed` actions. Do not modify unless you know
            what you are doing.*/
            embedStyle: { attribute: 'embed-style', type: String }, /*A list of dataset to observe. Will fire event `data` with the arguments
            `{name, value}`, where `name` is the name of the observed dataset, and
            `value` is the observed dataset.
            
            Only available for `vega@4`.*/
            observeData: { attribute: 'observe-data', type: asArray }, /*Set `observe-state` flag for the element to dispatch custom event
            `state` whenever the view is updated.*/
            observeState: { attribute: 'observe-state', type: Boolean }, };
    }
    firstUpdated() {
        this.ele = this.renderRoot.querySelector('#vis');
    }
    update(changedProperties) {
        super.update(changedProperties);
        const opts = Object.assign({}, this.opts);
        const { actions, showCompiled, showEditor, showExport, showSource } = this;
        const actionsOpt = typeof actions === 'boolean'
            ? actions
            : Object.assign({}, actions, { compiled: showCompiled, editor: showEditor, export: showExport, source: showSource });
        this.actions = actionsOpt;
        for (let [key] of changedProperties) {
            // do not add these properties
            if (IGNORED.has(key))
                continue;
            opts[key] = this[key];
        }
        this.opts = opts;
        this.debouncer = Debouncer.debounce(this.debouncer, microTask, this.updateEmbed.bind(this));
    }
    finalizeView(view) {
        this.unregisterDataListeners(view);
        view.finalize();
    }
    /**
     * Fired when the view is updated.
     *
     * @event view
     * @param {View} Vega view object.
     */
    /**
     * Update the view and register data and register data listeners.
     * @param {View} Vega view object.
     */
    updateView(view) {
        this.updateState(view);
        this.registerDataListeners(view);
        // dispatch event
        this.dispatchEvent(new CustomEvent('view', { detail: view }));
    }
    updateEmbed() {
        return __awaiter(this, void 0, void 0, function* () {
            const { spec, opts } = this;
            // do nothing if no spec is provided
            if (!spec)
                return;
            // clean up old view
            this.view && this.finalizeView(this.view);
            // update view
            this.view = yield this.getView(spec, opts);
            this.view && this.updateView(this.view);
        });
    }
    /**
     * Fired when error is encountered parsing or rendering the view.
     *
     * @event error
     * @param {Error} error object from Vega.
     */
    getView(spec, opts) {
        return __awaiter(this, void 0, void 0, function* () {
            // update
            try {
                const result = yield vegaEmbed(this.ele, spec, opts);
                return result.view;
            }
            catch (error) {
                this.dispatchEvent(new CustomEvent('error', { detail: error }));
                return undefined;
            }
        });
    }
    unregisterDataListeners(view) {
        // unregister old data listeners
        this.dataListeners.forEach(name => {
            try {
                view.removeDataListener(name, this.dataHandler);
            }
            catch (error) {
                this.dispatchEvent(new CustomEvent('error', { detail: error }));
            }
        });
    }
    registerDataListeners(view) {
        if (!this.observeData)
            return;
        this.dataListeners = this.observeData.map(name => {
            try {
                view.addDataListener(name, this.dataHandler);
            }
            catch (error) {
                this.dispatchEvent(new CustomEvent('error', { detail: error }));
            }
            return name;
        });
    }
    updateState(view) {
        if (!this.observeState)
            return;
        this.state = view.getState();
        this.dispatchEvent(new CustomEvent('state', { detail: this.state }));
    }
    render() {
        const { defaultStyle, embedStyle } = this;
        const style = typeof defaultStyle === 'string'
            ? `<style>${defaultStyle}</style>`
            : embedStyle;
        return html `
      <style>
        :host {
          display: block;
        }
        ${!!defaultStyle ? style : ''}
      </style>
      <div id="vis"></div>`;
    }
}
customElements.define('vega-embed', VegaEmbed);
export default VegaEmbed;
//# sourceMappingURL=vega-embed.js.map